import React, { useState, useRef, useEffect } from 'react';
import L from 'leaflet';
import { Map, PenTool, ShoppingCart, Info, Search, Truck, Package } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

const UC_RSBUYER_URL = 'https://secure.touchnet.net/C21575_ustores/rsbuyer';
const UC_PRODUCT_URL = 'https://secure.touchnet.net/C21575_ustores/web/product_detail.jsp?PRODUCTID=2231';

// Maps lowercase city name → UC Marketplace ITEM_MOD_0_4326 option value
const UC_CITY_VALUES = {
  'new york': '6899', 'houston': '6900', 'toronto': '6901', 'london': '6902',
  'paris': '6903', 'cincinnati': '6904', 'chicago': '6905', 'miami': '6906',
  'los angeles': '6907', 'las vegas': '6908', 'seattle': '6909', 'san francisco': '6910',
}; // '6911' = Custom Design (default)

const PRESET_CITIES = Object.keys(UC_CITY_VALUES).map(c => c.split(' ').map(w => w[0].toUpperCase() + w.slice(1)).join(' '));

const FORMSPREE_ID = 'mnjbejdd';

const STATE_ABBR = {
  'Alabama':'AL','Alaska':'AK','Arizona':'AZ','Arkansas':'AR','California':'CA',
  'Colorado':'CO','Connecticut':'CT','Delaware':'DE','Florida':'FL','Georgia':'GA',
  'Hawaii':'HI','Idaho':'ID','Illinois':'IL','Indiana':'IN','Iowa':'IA',
  'Kansas':'KS','Kentucky':'KY','Louisiana':'LA','Maine':'ME','Maryland':'MD',
  'Massachusetts':'MA','Michigan':'MI','Minnesota':'MN','Mississippi':'MS',
  'Missouri':'MO','Montana':'MT','Nebraska':'NE','Nevada':'NV','New Hampshire':'NH',
  'New Jersey':'NJ','New Mexico':'NM','New York':'NY','North Carolina':'NC',
  'North Dakota':'ND','Ohio':'OH','Oklahoma':'OK','Oregon':'OR','Pennsylvania':'PA',
  'Rhode Island':'RI','South Carolina':'SC','South Dakota':'SD','Tennessee':'TN',
  'Texas':'TX','Utah':'UT','Vermont':'VT','Virginia':'VA','Washington':'WA',
  'West Virginia':'WV','Wisconsin':'WI','Wyoming':'WY','District of Columbia':'DC',
  'Alberta':'AB','British Columbia':'BC','Manitoba':'MB','New Brunswick':'NB',
  'Newfoundland and Labrador':'NL','Nova Scotia':'NS','Ontario':'ON',
  'Prince Edward Island':'PE','Quebec':'QC','Saskatchewan':'SK',
};


// Gallery preview card — uses a real photo if `src` is provided, otherwise SVG placeholder
const GalleryCard = ({ city, subtitle, bg, block, road, src }) => (
  <div className="group cursor-pointer">
    <div className="w-full aspect-square rounded-xl overflow-hidden mb-4 shadow-md relative">
      {src ? (
        <img
          src={src}
          alt={city}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      ) : (
      <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full group-hover:scale-105 transition-transform duration-500">
        <rect width="200" height="200" fill={bg} />
        <rect x="0"   y="45"  width="200" height="5" fill={road} opacity="0.6" />
        <rect x="0"   y="95"  width="200" height="7" fill={road} opacity="0.8" />
        <rect x="0"   y="145" width="200" height="5" fill={road} opacity="0.6" />
        <rect x="45"  y="0"   width="5" height="200" fill={road} opacity="0.6" />
        <rect x="105" y="0"   width="7" height="200" fill={road} opacity="0.8" />
        <rect x="155" y="0"   width="5" height="200" fill={road} opacity="0.6" />
        <rect x="5"  y="5"  width="35" height="35" fill={block} opacity="0.3" rx="1" />
        <rect x="55" y="5"  width="45" height="35" fill={block} opacity="0.2" rx="1" />
        <rect x="118" y="5"  width="32" height="35" fill={block} opacity="0.3" rx="1" />
        <rect x="5"  y="55" width="35" height="35" fill={block} opacity="0.25" rx="1" />
        <rect x="55" y="55" width="45" height="35" fill={block} opacity="0.35" rx="1" />
        <rect x="118" y="55" width="32" height="35" fill={block} opacity="0.2" rx="1" />
        <rect x="165" y="55" width="30" height="35" fill={block} opacity="0.3" rx="1" />
        <rect x="5"  y="107" width="35" height="33" fill={block} opacity="0.3" rx="1" />
        <rect x="55" y="107" width="45" height="33" fill={block} opacity="0.2" rx="1" />
        <rect x="118" y="107" width="77" height="33" fill={block} opacity="0.3" rx="1" />
        <rect x="5"  y="155" width="35" height="40" fill={block} opacity="0.2" rx="1" />
        <rect x="55" y="155" width="45" height="40" fill={block} opacity="0.3" rx="1" />
        <rect x="118" y="155" width="32" height="40" fill={block} opacity="0.25" rx="1" />
        <rect x="165" y="155" width="30" height="40" fill={block} opacity="0.2" rx="1" />
      </svg>
      )}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
        <p className="font-serif font-bold text-white text-sm">{city}</p>
        <p className="text-white/70 text-xs">{subtitle}</p>
      </div>
    </div>
  </div>
);

export default function App() {
  const [locationInput, setLocationInput] = useState('New York, NY');
  const [mapCenter, setMapCenter] = useState([40.7128, -74.0060]);
  const [woodFinish, setWoodFinish] = useState('natural');
  const [addText, setAddText] = useState(false);
  const [customText, setCustomText] = useState('');
  const [shipping, setShipping] = useState('pickup');
  const [quantity, setQuantity] = useState(1);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  // Order contact details
  const [orderName, setOrderName] = useState('');
  const [orderEmail, setOrderEmail] = useState('');
  const [orderPhone, setOrderPhone] = useState('');

  // Shipping address (shown & required when shipping === 'usps')
  const [orderAddress1, setOrderAddress1] = useState('');
  const [orderAddress2, setOrderAddress2] = useState('');
  const [orderAddressCity, setOrderAddressCity] = useState('');
  const [orderPostalCode, setOrderPostalCode] = useState('');
  const [orderState, setOrderState] = useState('');
  const [orderCountry, setOrderCountry] = useState('US');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressSearching, setAddressSearching] = useState(false);
  const addressDebounceRef = useRef(null);

  // Viewport capture (tracks where user actually panned to)
  const [viewportCenter, setViewportCenter] = useState([40.7128, -74.0060]);
  const [viewportZoom, setViewportZoom] = useState(13);

  // Contact form
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactStatus, setContactStatus] = useState('idle'); // 'idle' | 'success' | 'error'

  const configuratorRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  const basePrice = 100;
  const textPrice = 0;
  const shippingPrice = shipping === 'usps' ? 9.5 : 0;
  const totalPrice = (basePrice * quantity) + textPrice + shippingPrice;

  const ucCity = PRESET_CITIES.find(c => locationInput.toLowerCase().includes(c.toLowerCase())) || 'Custom Design';
  const ucCustomization = addText ? 'Engraving' : 'Pin/Location Marker';
  const ucShipping = shipping === 'usps' ? 'USPS Ground Advantage Shipping (+$9.50)' : 'Local Pickup (+$0)';

  const scrollToConfigurator = () => {
    configuratorRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Initialize plain Leaflet map on mount
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const map = L.map(mapRef.current, {
      center: [40.7128, -74.0060],
      zoom: 13,
      scrollWheelZoom: true,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);

    map.on('moveend', () => {
      const c = map.getCenter();
      setViewportCenter([c.lat, c.lng]);
      setViewportZoom(map.getZoom());
    });

    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  // Re-center when mapCenter changes
  useEffect(() => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView(mapCenter, 13);
    }
  }, [mapCenter]);

  // Geocoding via OpenStreetMap Nominatim (free, no API key required)
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!locationInput.trim()) return;
    setIsSearching(true);
    setSearchError('');
    setSearchResults([]);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(locationInput)}&limit=5`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await res.json();
      if (data.length > 0) {
        setSearchResults(data);
      } else {
        setSearchError('Location not found. Try a more specific city or address.');
      }
    } catch {
      setSearchError('Search failed. Check your connection and try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectResult = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setMapCenter([lat, lng]);
    setViewportCenter([lat, lng]);
    setViewportZoom(13);
    setLocationInput(result.display_name);
    setSearchResults([]);
  };

  const handleAddressChange = (value) => {
    setOrderAddress1(value);
    clearTimeout(addressDebounceRef.current);
    if (value.length < 5) { setAddressSuggestions([]); return; }
    addressDebounceRef.current = setTimeout(async () => {
      setAddressSearching(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(value)}&addressdetails=1&limit=5`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const data = await res.json();
        setAddressSuggestions(data.filter(r => r.address));
      } catch {}
      setAddressSearching(false);
    }, 500);
  };

  const handleSelectAddress = (result) => {
    const a = result.address;
    const line1 = [a.house_number, a.road || a.pedestrian || a.path].filter(Boolean).join(' ');
    setOrderAddress1(line1 || result.display_name.split(',')[0]);
    setOrderAddressCity(a.city || a.town || a.village || a.municipality || '');
    setOrderPostalCode(a.postcode || '');
    const cc = (a.country_code || 'us').toUpperCase();
    setOrderCountry(cc);
    setOrderState(STATE_ABBR[a.state] || a.state || '');
    setAddressSuggestions([]);
  };

  const handleCheckout = () => {
    setShowCheckoutModal(true);
  };

  const handleConfirmCheckout = () => {
    const finishLabel = woodFinish === 'natural' ? 'Natural Wood' : 'Black Finish';
    const engravingLabel = addText ? (customText || '(text TBD)') : 'None';
    const shippingLabel = shipping === 'usps' ? 'USPS Ground Advantage (+$9.50)' : 'Local Pickup (Free)';

    // Fire-and-forget to Formspree for our own records
    fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        name: orderName, email: orderEmail, phone: orderPhone,
        city: locationInput, finish: finishLabel, engraving: engravingLabel,
        shipping: shippingLabel, quantity,
        total: `$${totalPrice.toFixed(2)}`,
        map_lat: viewportCenter[0].toFixed(6),
        map_lng: viewportCenter[1].toFixed(6),
        map_zoom: viewportZoom.toString(),
        ...(shipping === 'usps' && {
          address1: orderAddress1, address2: orderAddress2,
          addressCity: orderAddressCity, postalCode: orderPostalCode,
          state: orderState, country: orderCountry,
        }),
      }),
    }).then(async (res) => {
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        console.error('[Formspree checkout] Error:', res.status, res.statusText, body);
      } else {
        console.log('[Formspree checkout] Success');
      }
    }).catch((err) => console.error('[Formspree checkout] Network error:', err));

    if (shipping === 'pickup') {
      // rsbuyer does not support pickup on this UC Marketplace instance.
      // Open the product page directly — the modal above shows the user
      // exactly which options to select (city, customization, Local Pickup).
      window.open(UC_PRODUCT_URL, '_blank');
      setShowCheckoutModal(false);
      return;
    }

    const cityKey = Object.keys(UC_CITY_VALUES).find(k => locationInput.toLowerCase().includes(k));
    const ucCityValue = cityKey ? UC_CITY_VALUES[cityKey] : '6911';
    const ucCustomValue = addText ? '6914' : '6912'; // 6914=Engraving, 6912=Pin/Location Marker

    const form = document.createElement('form');
    form.method = 'post';
    form.action = UC_RSBUYER_URL;
    form.target = '_blank';

    const fields = {
      'tapp-stoken': 'null',
      START_APP: 'true',
      JSP_TYPE: 'web',
      Navigate: 'shopping_cart.jsp',
      OnError: 'product_detail.jsp?PRODUCTID=2231',
      ADD_PRODUCT_TO_CART: 'ActionKey',
      LANG_ID: '0',
      PRODUCTID: '2231',
      STOREID: '36',
      REDIRECT: 'true',
      QTY: String(quantity),
      ITEM_MOD_0_4326: ucCityValue,
      ITEM_MOD_0_4327: ucCustomValue,
      ITEM_MOD_0_4328: '6917', // USPS Ground Advantage
      ...(shipping === 'usps' && {
        ITEM_MOD_0_4329_FIELD_1: orderAddress1,
        ITEM_MOD_0_4329_FIELD_2: orderAddress2,
        ITEM_MOD_0_4329_FIELD_5: orderAddressCity,
        ITEM_MOD_0_4329_FIELD_7: orderState,
        ITEM_MOD_0_4329_FIELD_8: orderCountry,
        ITEM_MOD_0_4329_FIELD_9: orderPostalCode,
      }),
    };

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSubmitting(true);
    setContactStatus('idle');
    try {
      const res = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          name: contactName,
          email: contactEmail,
          city: locationInput,
          message: contactMessage,
          map_lat: viewportCenter[0].toFixed(6),
          map_lng: viewportCenter[1].toFixed(6),
          map_zoom: viewportZoom.toString(),
        }),
      });
      if (res.ok) {
        console.log('[Formspree contact] Success');
        setContactStatus('success');
        setContactName(''); setContactEmail(''); setContactMessage('');
      } else {
        const body = await res.text().catch(() => '');
        console.error('[Formspree contact] Error:', res.status, res.statusText, body);
        setContactStatus('error');
      }
    } catch (err) {
      console.error('[Formspree contact] Network error:', err);
      setContactStatus('error');
    } finally {
      setContactSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 text-ink font-sans selection:bg-wood selection:text-white">

      {/* Navigation */}
      <nav className="flex justify-between items-center p-6 max-w-7xl mx-auto">
        <div className="flex items-center space-x-2">
          <img src="/images/logo.png" alt="Northmark" className="w-10 h-10 rounded-lg" />
          <span className="text-2xl font-serif font-bold tracking-tight">Northmark</span>
        </div>
        <div className="hidden md:flex space-x-8 text-sm font-medium text-gray-600">
          <a href="#create" className="hover:text-ink transition-colors">Create Map</a>
          <a href="#gallery" className="hover:text-ink transition-colors">Gallery</a>
          <a href="#contact" className="hover:text-ink transition-colors">Custom Order</a>
          <a href="#about" className="hover:text-ink transition-colors">Our Story</a>
        </div>
        <button
          onClick={scrollToConfigurator}
          className="hidden md:flex items-center space-x-1 bg-ink text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-gray-800 transition-colors"
        >
          <ShoppingCart className="w-4 h-4 mr-1" />
          Order Now
        </button>
      </nav>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
        <div className="space-y-6">
          <div className="inline-block bg-wood-light/40 text-wood-dark text-sm font-semibold px-3 py-1 rounded-full">
            Handcrafted · 12"×12" · Laser-Cut Wood
          </div>
          <h1 className="text-5xl md:text-6xl font-serif font-bold leading-tight">
            Your City.<br />
            <span className="text-wood-dark">Carved in Wood.</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-md leading-relaxed">
            Celebrate where you met, where you grew up, or where your story began. Custom minimalist laser-cut city maps, handcrafted for your home.
          </p>
          <div className="flex items-center space-x-4 flex-wrap gap-y-3">
            <button
              onClick={scrollToConfigurator}
              className="bg-ink text-white px-8 py-4 rounded-md font-medium text-lg hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl flex items-center space-x-2"
            >
              <Map className="w-5 h-5" />
              <span>Design Your Map</span>
            </button>
            <p className="text-gray-500 text-sm">
              Starting at <span className="font-bold text-ink">$100</span>
            </p>
          </div>
        </div>
        <div className="relative h-96 md:h-[480px] w-full flex items-center justify-center">
          <img
            src="/images/Cincinnati.png"
            alt="Cincinnati laser-cut wooden map"
            className="w-full h-full object-cover drop-shadow-2xl rounded-2xl"
          />
          <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur px-4 py-2 rounded-full text-xs font-semibold shadow-md border border-gray-100">
            Epilog M2 Laser Cutter
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="border-y border-gray-200 bg-white py-7">
        <div className="max-w-4xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div><p className="font-bold text-ink text-lg">12"×12"</p><p className="text-sm text-gray-500">Perfect for any wall</p></div>
          <div><p className="font-bold text-ink text-lg">Any City</p><p className="text-sm text-gray-500">Any address worldwide</p></div>
          <div><p className="font-bold text-ink text-lg">2 Finishes</p><p className="text-sm text-gray-500">Natural or black stain</p></div>
          <div><p className="font-bold text-ink text-lg">Handmade</p><p className="text-sm text-gray-500">Laser-cut by our team</p></div>
        </div>
      </section>

      {/* Configurator Section */}
      <section id="create" ref={configuratorRef} className="bg-neutral-50 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-bold mb-4">Design Your Custom Map</h2>
            <p className="text-gray-600">Search any city, pick your finish, and add your personal touch.</p>
          </div>

          <div className="grid lg:grid-cols-12 gap-8">

            {/* Left: Map Preview */}
            <div className="lg:col-span-7 space-y-4">
              <div className="relative">
              <form onSubmit={handleSearch} className="flex space-x-2">
                <input
                  type="text"
                  value={locationInput}
                  onChange={(e) => { setLocationInput(e.target.value); setSearchResults([]); }}
                  placeholder="Enter a city or address..."
                  className="flex-1 border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-wood focus:border-transparent"
                />
                <button
                  type="submit"
                  disabled={isSearching}
                  className="bg-ink hover:bg-gray-800 text-white px-6 py-3 rounded-md font-medium transition-colors flex items-center space-x-2 disabled:opacity-60"
                >
                  <Search className="w-4 h-4" />
                  <span>{isSearching ? 'Searching…' : 'Search'}</span>
                </button>
              </form>

              {/* Search results dropdown */}
              {searchResults.length > 0 && (
                <ul className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                  {searchResults.map((result, i) => (
                    <li key={i}>
                      <button
                        type="button"
                        onClick={() => handleSelectResult(result)}
                        className="w-full text-left px-4 py-3 text-sm hover:bg-wood-light/30 transition-colors border-b border-gray-100 last:border-0"
                      >
                        <span className="font-medium text-ink line-clamp-1">{result.display_name}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {searchError && (
                <p className="text-red-500 text-sm mt-1">{searchError}</p>
              )}
            </div>

              <div
                className="w-full aspect-square rounded-xl overflow-hidden border-4 border-gray-100 shadow-inner relative z-0"
                style={{ minHeight: '320px' }}
              >
                <div ref={mapRef} style={{ height: '100%', width: '100%' }} />

                {/* 12"×12" crop guide overlay */}
                <div className="absolute inset-0 z-[300] pointer-events-none">
                  <div className="absolute inset-3 border-2 border-dashed border-white/50 rounded" />
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/40 text-white text-[10px] px-2 py-0.5 rounded-full font-medium">
                    12" × 12" print area
                  </div>
                </div>

                <div className="absolute top-4 right-4 z-[400] bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-medium border border-gray-200 shadow-sm">
                  Live Preview
                </div>
              </div>
              <p className="text-xs text-gray-400 text-center">
                Pan and zoom to frame your exact area. The dashed border shows your 12" × 12" print boundary.
              </p>
            </div>

            {/* Right: Options & Checkout */}
            <form onSubmit={(e) => { e.preventDefault(); handleCheckout(); }} className="lg:col-span-5 flex flex-col justify-between bg-white p-8 rounded-xl border border-gray-100 shadow-sm">
              <div className="space-y-7">

                {/* 1. Wood Finish */}
                <div>
                  <h3 className="text-base font-bold mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-ink text-white text-xs flex items-center justify-center font-bold">1</span>
                    Wood Finish
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <label className={`cursor-pointer border-2 rounded-lg p-3 text-center transition-all ${woodFinish === 'natural' ? 'border-wood bg-wood-light/20' : 'border-gray-200 hover:border-gray-300'}`}>
                      <input type="radio" name="finish" value="natural" className="hidden" onChange={() => setWoodFinish('natural')} defaultChecked />
                      <div className="w-full h-20 rounded-md mb-2" style={{ background: 'linear-gradient(135deg, #e6cda3 0%, #c19a6b 100%)' }} />
                      <span className="text-sm font-medium">Natural Wood</span>
                    </label>
                    <label className={`cursor-pointer border-2 rounded-lg p-3 text-center transition-all ${woodFinish === 'black' ? 'border-ink bg-gray-100' : 'border-gray-200 hover:border-gray-300'}`}>
                      <input type="radio" name="finish" value="black" className="hidden" onChange={() => setWoodFinish('black')} />
                      <div className="w-full h-20 rounded-md mb-2" style={{ background: 'linear-gradient(135deg, #2a2a2a 0%, #111 100%)' }} />
                      <span className="text-sm font-medium">Black Finish</span>
                    </label>
                  </div>
                </div>

                {/* 2. Custom Engraving */}
                <div>
                  <h3 className="text-base font-bold mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-ink text-white text-xs flex items-center justify-center font-bold">2</span>
                    <PenTool className="w-4 h-4" />
                    Custom Engraving
                  </h3>
                  <label className="flex items-center space-x-3 mb-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={addText}
                      onChange={(e) => setAddText(e.target.checked)}
                      className="w-5 h-5 rounded border-gray-300 accent-wood"
                    />
                    <span className="text-sm font-medium">Add personalized text below the map</span>
                  </label>
                  {addText && (
                    <div className="space-y-1">
                      <input
                        type="text"
                        value={customText}
                        onChange={(e) => setCustomText(e.target.value)}
                        placeholder='e.g., "Where it all began…"'
                        maxLength={40}
                        className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-wood focus:border-transparent"
                      />
                      <p className="text-xs text-gray-400 text-right">{customText.length}/40 characters</p>
                    </div>
                  )}
                </div>

                {/* 3. Shipping */}
                <div>
                  <h3 className="text-base font-bold mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-ink text-white text-xs flex items-center justify-center font-bold">3</span>
                    Delivery
                  </h3>
                  <div className="space-y-2">
                    <label className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${shipping === 'pickup' ? 'border-wood bg-wood-light/10' : 'border-gray-200 hover:border-gray-300'}`}>
                      <input type="radio" name="shipping" value="pickup" checked={shipping === 'pickup'} onChange={() => setShipping('pickup')} className="accent-wood" />
                      <Package className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">Local Pickup</p>
                        <p className="text-xs text-gray-500">Free · Cincinnati, OH</p>
                      </div>
                      <span className="text-sm font-bold text-green-600">Free</span>
                    </label>
                    <label className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${shipping === 'usps' ? 'border-wood bg-wood-light/10' : 'border-gray-200 hover:border-gray-300'}`}>
                      <input type="radio" name="shipping" value="usps" checked={shipping === 'usps'} onChange={() => setShipping('usps')} className="accent-wood" />
                      <Truck className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">USPS Ground Advantage</p>
                        <p className="text-xs text-gray-500">5–7 business days</p>
                      </div>
                      <span className="text-sm font-bold">+$9.50</span>
                    </label>
                  </div>
                </div>

                {/* 4. Quantity */}
                <div>
                  <h3 className="text-base font-bold mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-ink text-white text-xs flex items-center justify-center font-bold">4</span>
                    Quantity
                  </h3>
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={() => setQuantity(q => Math.max(1, q - 1))}
                      className="w-10 h-10 rounded-lg border-2 border-gray-200 hover:border-gray-400 flex items-center justify-center text-lg font-bold transition-colors"
                    >−</button>
                    <span className="w-12 text-center text-lg font-bold">{quantity}</span>
                    <button
                      type="button"
                      onClick={() => setQuantity(q => Math.min(10, q + 1))}
                      className="w-10 h-10 rounded-lg border-2 border-gray-200 hover:border-gray-400 flex items-center justify-center text-lg font-bold transition-colors"
                    >+</button>
                    <span className="text-sm text-gray-500 ml-2">max 10 per order</span>
                  </div>
                </div>

                {/* 5. Your Details */}
                <div>
                  <h3 className="text-base font-bold mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-ink text-white text-xs flex items-center justify-center font-bold">5</span>
                    Your Details
                  </h3>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1 text-gray-600">Name *</label>
                        <input
                          type="text"
                          required
                          value={orderName}
                          onChange={(e) => setOrderName(e.target.value)}
                          placeholder="Jane Smith"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-wood focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1 text-gray-600">Phone</label>
                        <input
                          type="tel"
                          value={orderPhone}
                          onChange={(e) => setOrderPhone(e.target.value)}
                          placeholder="(513) 555-0100"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-wood focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1 text-gray-600">Email *</label>
                      <input
                        type="email"
                        required
                        value={orderEmail}
                        onChange={(e) => setOrderEmail(e.target.value)}
                        placeholder="jane@example.com"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-wood focus:border-transparent"
                      />
                    </div>

                    {/* Shipping address — only when USPS selected */}
                    {shipping === 'usps' && (
                      <div className="space-y-2 pt-1 border-t border-gray-100">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-1">Shipping Address</p>
                        <div className="relative">
                          <input
                            type="text"
                            required
                            value={orderAddress1}
                            onChange={(e) => handleAddressChange(e.target.value)}
                            placeholder="Address Line 1 *"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-wood focus:border-transparent"
                            autoComplete="off"
                          />
                          {addressSearching && (
                            <span className="absolute right-3 top-2.5 text-xs text-gray-400">Searching…</span>
                          )}
                          {addressSuggestions.length > 0 && (
                            <ul className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                              {addressSuggestions.map((result, i) => (
                                <li key={i}>
                                  <button
                                    type="button"
                                    onClick={() => handleSelectAddress(result)}
                                    className="w-full text-left px-3 py-2.5 text-xs hover:bg-wood-light/30 transition-colors border-b border-gray-100 last:border-0"
                                  >
                                    <span className="font-medium text-ink line-clamp-1">{result.display_name}</span>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <input
                          type="text"
                          value={orderAddress2}
                          onChange={(e) => setOrderAddress2(e.target.value)}
                          placeholder="Address Line 2 (optional)"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-wood focus:border-transparent"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            required
                            value={orderAddressCity}
                            onChange={(e) => setOrderAddressCity(e.target.value)}
                            placeholder="City *"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-wood focus:border-transparent"
                          />
                          <input
                            type="text"
                            required
                            value={orderPostalCode}
                            onChange={(e) => setOrderPostalCode(e.target.value)}
                            placeholder="Postal Code *"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-wood focus:border-transparent"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            required
                            value={orderState}
                            onChange={(e) => setOrderState(e.target.value)}
                            placeholder="State/Province *"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-wood focus:border-transparent"
                          />
                          <select
                            value={orderCountry}
                            onChange={(e) => setOrderCountry(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-wood focus:border-transparent bg-white"
                          >
                            <option value="US">United States</option>
                            <option value="CA">Canada</option>
                            <option value="GB">United Kingdom</option>
                            <option value="AU">Australia</option>
                            <option value="MX">Mexico</option>
                            <option value="FR">France</option>
                            <option value="DE">Germany</option>
                            <option value="JP">Japan</option>
                            <option value="BR">Brazil</option>
                            <option value="IN">India</option>
                            <option value="KR">South Korea</option>
                            <option value="NL">Netherlands</option>
                            <option value="SE">Sweden</option>
                            <option value="NO">Norway</option>
                            <option value="CH">Switzerland</option>
                            <option value="NZ">New Zealand</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Price Summary & Checkout */}
              <div className="border-t border-gray-200 pt-6 mt-6">
                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Base Map (12"×12") × {quantity}</span>
                    <span>${(100 * quantity).toFixed(2)}</span>
                  </div>
                  {addText && (
                    <div className="flex justify-between text-gray-600">
                      <span>Custom Engraving</span>
                      <span>Free</span>
                    </div>
                  )}
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping</span>
                    <span>{shippingPrice === 0 ? 'Free' : '+$9.50'}</span>
                  </div>
                  <div className="flex justify-between text-xl font-bold pt-3 border-t border-gray-100 text-ink">
                    <span>Total</span>
                    <span>${totalPrice.toFixed(2)}</span>
                  </div>
                </div>
                <button
                  type="submit"
                  className="w-full bg-wood hover:bg-wood-dark text-white py-4 rounded-md font-bold text-lg transition-colors flex justify-center items-center space-x-2 shadow-md"
                >
                  <ShoppingCart className="w-5 h-5" />
                  <span>Order Now — ${totalPrice.toFixed(2)}</span>
                </button>
                <div className="mt-3 flex items-start space-x-2 text-xs text-gray-500">
                  <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <p>We'll confirm your order, then pre-fill your selections on UC Marketplace to complete payment.</p>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-serif font-bold text-center mb-4">Example Maps</h2>
          <p className="text-center text-gray-500 mb-12">Every map is generated to order — your city, your style.</p>
          <div className="grid md:grid-cols-3 gap-8">
            <GalleryCard city="Cincinnati, OH" subtitle="Natural Wood Finish" src="/images/Cincinnati.png" />
            <GalleryCard city="Houston, TX"    subtitle="Natural Wood Finish" src="/images/Houston.png" />
            <GalleryCard city="Ithaca, NY"     subtitle="Natural Wood Finish" src="/images/Ithaca, NY.png" />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-neutral-50 border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-serif font-bold mb-12">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Choose Your City', desc: 'Enter any city or address. Our tool pulls the real street-map layout for your location.' },
              { step: '02', title: 'Customize',         desc: 'Pick a wood finish and optionally add a short personal engraving below the map.' },
              { step: '03', title: 'We Craft It',       desc: 'Your map is laser-cut on our Epilog M2, then ready for pickup or shipped to you.' },
            ].map(({ step, title, desc }) => (
              <div key={step} className="space-y-3">
                <div className="text-4xl font-serif font-bold text-wood-light">{step}</div>
                <h3 className="font-bold text-lg">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / Custom Order Section */}
      <section id="contact" className="py-20 bg-white border-t border-gray-100">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-serif font-bold mb-3">Custom Order</h2>
            <p className="text-gray-600">Have a special request? A specific neighborhood, unusual size, or gift idea? Send us a message and we'll work it out.</p>
          </div>

          {contactStatus === 'success' ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center space-y-3">
              <div className="text-3xl">✓</div>
              <h3 className="font-bold text-green-800 text-lg">Message sent!</h3>
              <p className="text-green-700 text-sm">We'll get back to you within 24 hours. Check your inbox.</p>
              <button
                onClick={() => setContactStatus('idle')}
                className="mt-2 text-sm text-green-700 underline"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleContactSubmit} className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium mb-1.5">Your Name</label>
                  <input
                    type="text"
                    required
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="Jane Smith"
                    className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-wood focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">Email Address</label>
                  <input
                    type="email"
                    required
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="jane@example.com"
                    className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-wood focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5">City / Location</label>
                <input
                  type="text"
                  readOnly
                  value={locationInput}
                  className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm bg-gray-50 text-gray-500 cursor-default"
                />
                <p className="text-xs text-gray-400 mt-1">Set from your map preview above. Search a city first to change this.</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5">Tell us about your order</label>
                <textarea
                  required
                  rows={5}
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  placeholder="e.g., I'd like a map centered on the Eiffel Tower area with a specific neighborhood boundary, and a quote engraved underneath..."
                  className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-wood focus:border-transparent resize-none"
                />
              </div>

              {contactStatus === 'error' && (
                <p className="text-red-500 text-sm">Something went wrong. Please try again or email us directly.</p>
              )}

              <button
                type="submit"
                disabled={contactSubmitting}
                className="w-full bg-ink hover:bg-gray-800 text-white py-3 rounded-md font-medium transition-colors disabled:opacity-60"
              >
                {contactSubmitting ? 'Sending…' : 'Send Message'}
              </button>

              <p className="text-xs text-gray-400 text-center">
                We'll also receive your current map coordinates so we can reproduce your exact view.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-ink text-white">
        <div className="max-w-4xl mx-auto px-6 text-center space-y-6">
          <img src="/images/logo.png" alt="Northmark" className="w-16 h-16 rounded-xl mx-auto" />
          <h2 className="text-3xl font-serif font-bold">About Northmark</h2>
          <p className="text-lg text-gray-300 leading-relaxed max-w-2xl mx-auto">
            We're a three-person team who believe that places hold our most powerful memories. Northmark was born from a desire to turn digital map data into tangible, beautiful wooden art. Every piece is handcrafted in Cincinnati using our Epilog M2 laser cutter.
          </p>
          <button
            onClick={scrollToConfigurator}
            className="inline-flex items-center space-x-2 bg-wood hover:bg-wood-dark text-white px-8 py-3 rounded-md font-medium transition-colors"
          >
            <Map className="w-4 h-4" />
            <span>Start Your Map</span>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-ink border-t border-gray-800 text-gray-500 py-8 text-center text-sm">
        <p>&copy; {new Date().getFullYear()} Northmark Maps. All rights reserved.</p>
      </footer>

      {/* Checkout Confirmation Modal */}
      {showCheckoutModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowCheckoutModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 space-y-6">

            {/* Header */}
            <div className="text-center space-y-1">
              <div className="w-12 h-12 bg-wood-light/40 rounded-full flex items-center justify-center mx-auto mb-3">
                <ShoppingCart className="w-6 h-6 text-wood-dark" />
              </div>
              <h2 className="text-xl font-serif font-bold">Ready to checkout!</h2>
              <p className="text-sm text-gray-500">
                {shipping === 'pickup'
                  ? 'Review your order below, then select the same options on UC Marketplace.'
                  : 'Your selections will be pre-filled on UC Marketplace.'}
              </p>
            </div>

            {/* Order summary rows */}
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg border border-gray-100">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                  <Map className="w-4 h-4" />
                  <span>City</span>
                </div>
                <span className="text-sm font-bold text-ink bg-wood-light/30 px-3 py-1 rounded-full">{ucCity}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg border border-gray-100">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                  <PenTool className="w-4 h-4" />
                  <span>Customization</span>
                </div>
                <span className="text-sm font-bold text-ink bg-wood-light/30 px-3 py-1 rounded-full">{ucCustomization}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg border border-gray-100">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                  <Truck className="w-4 h-4" />
                  <span>Shipping</span>
                </div>
                <span className="text-sm font-bold text-ink bg-wood-light/30 px-3 py-1 rounded-full">{ucShipping}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg border border-gray-100">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                  <Package className="w-4 h-4" />
                  <span>Quantity</span>
                </div>
                <span className="text-sm font-bold text-ink bg-wood-light/30 px-3 py-1 rounded-full">{quantity}</span>
              </div>
            </div>

            {/* Total */}
            <div className="flex justify-between items-center py-3 border-t border-gray-100 text-sm">
              <span className="text-gray-500">Order total</span>
              <span className="font-bold text-ink text-base">${totalPrice.toFixed(2)}</span>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <button
                onClick={handleConfirmCheckout}
                className="w-full bg-wood hover:bg-wood-dark text-white py-3.5 rounded-lg font-bold transition-colors flex items-center justify-center gap-2 shadow-md"
              >
                <ShoppingCart className="w-5 h-5" />
                {shipping === 'pickup' ? 'Go to UC Marketplace →' : 'Complete Order on UC Marketplace'}
              </button>
              <p className="text-center text-xs text-gray-400">
                {shipping === 'pickup'
                  ? 'Opens the product page — select the options shown above'
                  : 'Opens in a new tab — your selections will be pre-filled'}
              </p>
              <button
                onClick={() => setShowCheckoutModal(false)}
                className="w-full text-gray-500 hover:text-ink text-sm py-2 transition-colors"
              >
                ← Go back and edit my order
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
